import { type ElementType, type ReactNode, forwardRef } from "react";
import { useReveal } from "@/lib/useReveal";

type RevealProps = {
  children: ReactNode;
  className?: string;
  as?: ElementType;
  delay?: 0 | 1 | 2 | 3 | 4 | 5;
  reversible?: boolean;
};

/**
 * Wrapper que aplica `.reveal` + `.in` quando entra na viewport.
 * `delay` 1..5 ativa as classes `.d1`..`.d5` definidas em globals.css.
 */
export function Reveal({
  children,
  className = "",
  as: Tag = "div",
  delay = 0,
  reversible = false,
}: RevealProps) {
  const [ref, vis] = useReveal<HTMLElement>(reversible);
  const dCls = delay > 0 ? `d${delay}` : "";
  const cls = ["reveal", dCls, vis ? "in" : "", className].filter(Boolean).join(" ");
  return <Tag ref={ref} className={cls}>{children}</Tag>;
}

type RevealWordsProps = {
  text: string;
  className?: string;
  as?: ElementType;
};

/**
 * Quebra texto em palavras e revela cada uma com stagger.
 * Reproduz o comportamento de `RevealWords` do home.jsx (reversível ao scroll).
 */
export const RevealWords = forwardRef<HTMLElement, RevealWordsProps>(function RevealWords(
  { text, className = "", as: Tag = "p" },
  _outerRef,
) {
  const [ref, vis] = useReveal<HTMLElement>(true);
  const words = text.split(" ");
  const cls = ["rw", vis ? "in" : "", className].filter(Boolean).join(" ");
  return (
    <Tag ref={ref} className={cls}>
      {words.map((w, i) => (
        <span
          key={i}
          className="rw-w"
          style={{ transitionDelay: `${Math.min(i * 0.02, 0.55)}s` }}
        >
          {w}
        </span>
      ))}
    </Tag>
  );
});
